
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Add a new page pricing metric
 * @param slug The company slug
 * @param displayName The display name for the metric
 * @param pricingMetricId The ID of the associated pricing metric
 * @param sampleValues Optional array of sample values
 * @param modality Optional modality type
 * @param quarter Optional quarter value
 * @returns Promise with the newly created page pricing metric or null if failed
 */
export const addPagePricingMetric = async (
  slug: string,
  displayName: string,
  pricingMetricId: string,
  sampleValues?: string[],
  modality?: "Flat" | "License" | "Credit" | "Usage" | "Threshold" | "Unknown" | null,
  quarter?: string | null
) => {
  try {
    if (!slug || !displayName || !pricingMetricId) {
      throw new Error('Missing required fields: slug, displayName, or pricingMetricId');
    }

    // Create the new page pricing metric
    const { data: newMetric, error } = await supabase
      .from('page_pricing_metrics')
      .insert({
        slug,
        display_name: displayName,
        pricing_metric_id: pricingMetricId,
        sample_values: sampleValues || [],
        modality,
        quarter
      })
      .select('id, slug, display_name, pricing_metric_id, sample_values, modality, quarter, created_at')
      .single();

    if (error) {
      console.error('Error adding page pricing metric:', error);
      toast.error(`Failed to add metric: ${error.message}`);
      return null;
    }

    toast.success('Page pricing metric added successfully');
    return newMetric;
  } catch (error) {
    console.error('Error in addPagePricingMetric:', error);
    toast.error(`Failed to add metric: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return null;
  }
};

/**
 * Create a new pricing metric
 * @param name The name of the metric
 * @param subcategory Optional subcategory
 * @param modality Optional modality type
 * @returns Promise with the newly created pricing metric or null if failed
 */
export const createPricingMetric = async (
  name: string,
  subcategory: string | null = null,
  modality: "Flat" | "License" | "Credit" | "Usage" | "Threshold" | "Unknown" | null = null
) => {
  try {
    if (!name) {
      throw new Error('Missing required field: name');
    }

    // Create the new pricing metric
    const { data: newMetric, error } = await supabase
      .from('pricing_metrics')
      .insert({
        name,
        subcategory,
        modality
      })
      .select('id, name, subcategory, modality')
      .single();

    if (error) {
      console.error('Error creating pricing metric:', error);
      toast.error(`Failed to create metric: ${error.message}`);
      return null;
    }

    toast.success('Pricing metric created successfully');
    return newMetric;
  } catch (error) {
    console.error('Error in createPricingMetric:', error);
    toast.error(`Failed to create metric: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return null;
  }
};

/**
 * Map a page pricing metric to a specific version
 * @param pageMetricId The ID of the page pricing metric
 * @param slug The company slug
 * @param version The version to map to
 * @returns Promise with the newly created mapping or null if failed
 */
export const mapPagePricingMetricToVersion = async (
  pageMetricId: string,
  slug: string,
  version: string
) => {
  try {
    if (!pageMetricId || !slug || !version) {
      throw new Error('Missing required fields: pageMetricId, slug, or version');
    }

    // Create the mapping
    const { data: newMapping, error } = await supabase
      .from('page_pricing_metrics_map')
      .insert({
        page_pricing_metrics_id: pageMetricId,
        slug,
        version
      })
      .select('id, slug, version, page_pricing_metrics_id, created_at')
      .single();

    if (error) {
      console.error('Error mapping page pricing metric to version:', error);
      toast.error(`Failed to map metric to version: ${error.message}`);
      return null;
    }

    toast.success('Metric mapped to version successfully');
    return newMapping;
  } catch (error) {
    console.error('Error in mapPagePricingMetricToVersion:', error);
    toast.error(`Failed to map metric to version: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return null;
  }
};

/**
 * Get existing pricing metrics for dropdown selection
 * @returns Promise with array of pricing metrics
 */
export const getPricingMetrics = async () => {
  try {
    const { data, error } = await supabase
      .from('pricing_metrics')
      .select('id, name, subcategory, modality')
      .order('name');

    if (error) {
      console.error('Error fetching pricing metrics:', error);
      toast.error(`Failed to fetch pricing metrics: ${error.message}`);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getPricingMetrics:', error);
    toast.error(`Failed to fetch pricing metrics: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return [];
  }
};
